import { PageContainer } from '@/components/containers/page-container';
import { Section } from '@/components/containers/section';
import styles from './patient-information-page.module.css';
import { PatientInformation } from '@/components/person-information/patient-information';
import { SurrogateInformation } from '@/components/person-information/surrogate-information';
import { useGetPatient } from '@/api/get-personal-info';
import { useLoggedUser } from '@/contexts/current-user-context';
import { getAge } from '@/util/helpers';
import { NotificationsInformation } from '@/components/person-information/notifications-information';
import { TwoFactorInformation } from '@/components/person-information/two-factor-information';
import { NavButton } from '@/components/navigation/nav-button';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useTranslation } from '@/contexts/i18n-context';
import { Loader } from '@/components/loaders/loader';
import { Banner } from '@/components/banner';
import { useIsVisitInProgress } from '@/hooks/use-is-visit-in-progress';
import { useBranding } from '@/outlets/branding-outlet';

export function PatientInformationPage() {
  const { t } = useTranslation();
  const { user } = useLoggedUser();
  const { isVisitInProgress, isFetching: isFetchingVisitInProgress } = useIsVisitInProgress();
  const { data: patient, isLoading: isLoadingPatient, refetch } = useGetPatient(user.id);
  const { loginPageDisabled } = useBranding();
  const isMobile = useMediaQuery('--screen-md');

  const patientAge = getAge(patient?.dateOfBirth);
  const isSurrogateOptional = !patient?.dateOfBirth || (patientAge != null && patientAge >= 18);

  if (isFetchingVisitInProgress) {
    return <Loader type="large" />;
  }

  return (
    <PageContainer>
      {!isMobile && (
        <NavButton
          className={styles.backButton}
          type="back"
          text="patientInformation.backToDashboard"
          to="/"
        />
      )}
      <GradientFormTitle>
        {isMobile && <NavButton type="back" text="patientInformation.backToDashboard" to="/" />}
        <h1 className="h3">{t('patientInformation.title')}</h1>
      </GradientFormTitle>
      <Section className={styles.patientInformationContainer}>
        {isVisitInProgress && (
          <Banner type="info">
            <span className="semibold">{t('patientInformation.cannotEditProfileBanner')}</span>
          </Banner>
        )}

        <PatientInformation
          patient={patient}
          isFetchingPatient={isLoadingPatient}
          canEdit={!isVisitInProgress}
        />

        <div className={styles.divider} />

        <SurrogateInformation
          surrogate={patient?.surrogate}
          isFetchingSurrogate={isLoadingPatient}
          onUpdate={refetch}
          type={isSurrogateOptional ? 'optional' : 'mandatory'}
          isPatientCompleted={!!patient?.dateOfBirth}
          canEdit={!isVisitInProgress}
        />

        <div className={styles.divider} />

        {!loginPageDisabled && (
          <>
            <TwoFactorInformation
              patient={patient}
              isFetchingPatient={isLoadingPatient}
              onUpdate={refetch}
            />
            <div className={styles.divider} />
          </>
        )}

        <NotificationsInformation
          patient={patient}
          isFetchingPatient={isLoadingPatient}
          surrogate={patient?.surrogate}
        />
      </Section>
    </PageContainer>
  );
}
